/**
 * Foundation Auth Store
 *
 * Zustand store for foundation/funder portal authentication. Mirrors the
 * Vendor pattern (shared/stores/vendorAuthStore.ts) — same JWT/access/
 * refresh shape, same persistence model, different localStorage key.
 *
 * The JWT carries sponsorId/foundationId; backend's requireFoundationContext
 * (routes/foundation/index.js:48) extracts and gates on it. Per §A3 invariant,
 * every sponsor-scoped read uses req.foundation.sponsorId — NEVER URL/body.
 */

import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface FoundationUser {
  userId: string;
  email?: string;
  display_name?: string;
  role?: string;             // 'foundation_admin' | 'foundation_counselor' | 'foundation_member'
  sponsorId: string;         // foundation/sponsor identity — the gate axis
  sponsor_id?: string;       // backwards-compat alias
  foundationId?: string;     // alias
  sponsorName?: string;
  totp_enabled?: boolean;
}

export interface FoundationAuthState {
  user: FoundationUser | null;
  token: string | null;
  refreshToken: string | null;
  isAuthenticated: boolean;
  loggedInAt: number | null;
  accessExpiresAt: number | null;
  refreshExpiresAt: string | null;
  /**
   * 2FA verified flag for the current session. The server mints a
   * full-strength token only after 2FA; this flag is preserved here for
   * client-side hints (e.g., gating the Step-Up button).
   */
  twoFactorVerified: boolean;
}

export interface FoundationAuthActions {
  setUserWithSession: (
    user: FoundationUser,
    accessToken: string,
    refreshToken: string | null,
    expiresIn: number,
    refreshExpiresAt: string | null
  ) => void;
  setUser: (user: FoundationUser, token: string) => void;
  updateUser: (updates: Partial<FoundationUser>) => void;
  mark2FAVerified: () => void;
  logout: () => void;
  isExpired: () => boolean;
}

const initialState: FoundationAuthState = {
  user: null,
  token: null,
  refreshToken: null,
  isAuthenticated: false,
  loggedInAt: null,
  accessExpiresAt: null,
  refreshExpiresAt: null,
  twoFactorVerified: false,
};

export const useFoundationAuthStore = create<FoundationAuthState & FoundationAuthActions>()(
  persist(
    (set, get) => ({
      ...initialState,
      setUserWithSession: (user, accessToken, refreshToken, expiresIn, refreshExpiresAt) =>
        set({
          user,
          token: accessToken,
          refreshToken,
          isAuthenticated: true,
          loggedInAt: Date.now(),
          accessExpiresAt: Date.now() + (expiresIn * 1000),
          refreshExpiresAt,
          twoFactorVerified: !user.totp_enabled,
        }),
      setUser: (user, token) =>
        set({
          user,
          token,
          isAuthenticated: true,
          loggedInAt: Date.now(),
          accessExpiresAt: null,
          refreshToken: null,
          refreshExpiresAt: null,
          twoFactorVerified: true,
        }),
      updateUser: (updates) =>
        set((state) => ({
          user: state.user ? { ...state.user, ...updates } : null,
        })),
      mark2FAVerified: () => set({ twoFactorVerified: true }),
      logout: () => set(initialState),
      isExpired: () => {
        const { accessExpiresAt } = get();
        if (!accessExpiresAt) return false;
        return Date.now() >= accessExpiresAt;
      },
    }),
    {
      name: 'agenticed_foundation_auth',
      storage: createJSONStorage(() => localStorage),
    }
  )
);

export function isFoundationAuthenticated(): boolean {
  const state = useFoundationAuthStore.getState();
  return state.isAuthenticated && !!state.token && !state.isExpired();
}
